import { useState, useEffect } from 'react';
import { X, Share, Plus, MoreVertical, Download } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export default function InstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Don't show install prompt in native app
    if (Capacitor.isNativePlatform()) {
      return;
    }

    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const daysSince = (Date.now() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return; // Don't show for 7 days after dismiss
    }

    // Listen for beforeinstallprompt (Chrome/Android)
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // Show iOS prompt after delay
    if (ios && !standalone) {
      setTimeout(() => setShowPrompt(true), 3000);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', new Date().toISOString());
  };

  // Don't render in native app
  if (Capacitor.isNativePlatform()) return null;
  
  if (!showPrompt || isStandalone) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-slide-up">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-xl bg-hub-100 flex items-center justify-center flex-shrink-0">
            <Download className="w-6 h-6 text-hub-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900">Install Hubtractor</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add to your home screen for quick access and a better experience.
            </p>

            {isIOS ? (
              <div className="mt-3 p-3 bg-gray-50 rounded-lg text-sm">
                <p className="font-medium text-gray-700 mb-2">To install on iPhone/iPad:</p>
                <ol className="space-y-2 text-gray-600">
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center text-xs font-bold">1</span>
                    Tap <Share className="w-4 h-4 inline mx-1" /> Share button
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center text-xs font-bold">2</span>
                    Scroll down, tap <Plus className="w-4 h-4 inline mx-1" /> "Add to Home Screen"
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-hub-100 text-hub-600 flex items-center justify-center text-xs font-bold">3</span>
                    Tap "Add"
                  </li>
                </ol>
              </div>
            ) : (
              <div className="mt-3 flex gap-2">
                <button onClick={handleInstall} className="btn-primary text-sm py-2 px-4">
                  Install App
                </button>
                <button onClick={handleDismiss} className="btn-secondary text-sm py-2 px-4">
                  Not Now
                </button>
              </div>
            )}
          </div>
          <button 
            onClick={handleDismiss} 
            className="p-1 hover:bg-gray-100 rounded-lg flex-shrink-0"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>
    </div>
  );
}
